import React, {
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useMemo,
  useState,
  cloneElement
} from "react";
import { cx } from "@linaria/core";
import { SelectProvider, SelectContext } from "../state";
import { useOutsideClickRef, useKeyboardEvents } from "../hooks";
import {
  OptionsContainer,
  SelectInput,
  CaretDown,
  CaretUp,
  SelectWrapper,
  SelectCaption
} from "./Select.styles";
import SelectSearchInput from "./SelectSearchInput";
import SelectAllOption from "./SelectAllOption";
export const BaseSelect = ({
  id,
  children,
  disabled = false,
  className,
  label,
  style,
  error,
  touched,
  caption,
  testId = "Select",
  forwardedRef,
  displayValueTransform = (f) => f,
  searchable = false,
  selectAllOption,
  onBlur = () => {
  },
  mode = "light",
  onOpen = () => {
  },
  ...restProps
}) => {
  const {
    isExpanded,
    isMultiselect,
    setIsExpanded,
    selectedValue,
    selectedValues
  } = useContext(SelectContext);
  const [showError, setShowError] = useState(false);
  const clearMultiselectHoveredStyle = () => {
    const activeHovered = multiselectHoveredOption?.current;
    const activeKeyClassname = "is-key-active";
    if (activeHovered?.classList.contains(activeKeyClassname)) {
      activeHovered.classList.remove(activeKeyClassname);
    }
  };
  useEffect(() => {
    if (error) {
      if (typeof touched === "boolean") {
        if (touched)
          setShowError(true);
      } else {
        setShowError(true);
      }
    } else {
      setShowError(false);
    }
  }, [error, touched]);
  useEffect(() => {
    if (isExpanded && isMultiselect) {
      const listEl = listRef.current;
      if (listEl) {
        listEl.scrollTo({
          top: 0,
          behavior: "smooth"
        });
      }
    }
    if (!isExpanded) {
      clearMultiselectHoveredStyle();
      multiselectIndex.current = -1;
    }
  }, [isExpanded, isMultiselect]);
  useEffect(() => {
    if (isExpanded) {
      const elemId = `${id}_${selectedValue}`;
      let offset = document.getElementById(elemId)?.offsetTop;
      const listEl = listRef.current;
      const allOptionsEl = allOptionsRef.current;
      if (allOptionsEl?.ariaSelected === "true") {
        offset = allOptionsEl?.offsetTop;
      }
      if (listEl && typeof offset !== "undefined") {
        listEl.scrollTo({ top: offset, behavior: "smooth" });
      }
    }
  }, [selectedValue]);
  const classNames = cx(isExpanded && "is-expanded", (selectedValue || selectedValues.length > 0) && "has-selection", disabled && "is-disabled", showError && "has-error");
  const labelId = `${id}_label`;
  const selectedValueId = `${id}_selected`;
  const listId = `${id}_list`;
  const ownRef = useRef(null);
  const buttonRef = forwardedRef || ownRef;
  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  const allOptionsRef = useRef(null);
  const childrenRef = useRef([]);
  const multiselectIndex = useRef(-1);
  const multiselectHoveredOption = useRef(null);
  const keyboardEvents = useKeyboardEvents({
    initRef: buttonRef,
    listRef,
    searchInputRef,
    children,
    childrenRef,
    context: SelectContext,
    multiselectIndex,
    multiselectHoveredOption
  });
  const collapse = () => isExpanded && setIsExpanded(false);
  const outsideClickRef = useOutsideClickRef(collapse);
  const handleOnClick = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (!isExpanded)
      onOpen();
    buttonRef?.current?.focus();
    setIsExpanded(!isExpanded);
  };
  const disableUpArrowScroll = (event) => {
    const { key } = event;
    if (key === "ArrowUp") {
      if (event.cancelable)
        event.preventDefault();
    }
  };
  const displayValue = useMemo(() => {
    const multiselectValue = selectedValues.length > 1 ? `${selectedValues[selectedValues.length - 1]}, +${selectedValues?.length - 1} more...` : selectedValues[0];
    return displayValueTransform(isMultiselect ? multiselectValue : selectedValue, isMultiselect ? selectedValues : [selectedValue]);
  }, [displayValueTransform, isMultiselect, selectedValue, selectedValues]);
  const selectAllConfig = useMemo(() => {
    const isString = typeof selectAllOption === "string";
    return {
      enable: isString ? true : Boolean(selectAllOption ?? isMultiselect),
      label: isString ? selectAllOption : "Choose All"
    };
  }, [selectAllOption, isMultiselect]);
  return /* @__PURE__ */ React.createElement(SelectWrapper, {
    ref: outsideClickRef,
    style: { ...style },
    className: cx(className, "select", mode === "dark" && "dark-mode"),
    onKeyUp: keyboardEvents,
    onKeyDown: disableUpArrowScroll,
    "data-testid": testId
  }, /* @__PURE__ */ React.createElement(SelectInput, {
    onClick: handleOnClick,
    "aria-haspopup": "listbox",
    "aria-labelledby": `${labelId} ${selectedValueId}`,
    id,
    ref: buttonRef,
    className: classNames,
    type: "button",
    ...isExpanded && { "aria-expanded": true },
    ...restProps
  }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("div", {
    id: labelId,
    className: "select__label"
  }, label), /* @__PURE__ */ React.createElement("span", {
    id: selectedValueId,
    className: "select__inputValue"
  }, displayValue)), isExpanded ? /* @__PURE__ */ React.createElement(CaretUp, null) : /* @__PURE__ */ React.createElement(CaretDown, {
    disabled
  })), caption && /* @__PURE__ */ React.createElement(SelectCaption, null, caption), showError && /* @__PURE__ */ React.createElement(SelectCaption, {
    className: "has-error"
  }, error), /* @__PURE__ */ React.createElement(OptionsContainer, {
    role: "listbox",
    className: classNames,
    "aria-labelledby": labelId,
    id: listId,
    ...isExpanded && selectedValue && {
      "aria-activedescendant": `${id}_${selectedValue}`
    },
    ...isMultiselect && {
      "aria-multiselectable": true
    },
    tabIndex: -1,
    ref: listRef,
    onMouseOver: clearMultiselectHoveredStyle,
    onBlur
  }, !!searchable && /* @__PURE__ */ React.createElement(SelectSearchInput, {
    ref: searchInputRef,
    callback: typeof searchable === "function" ? searchable : void 0
  }), isMultiselect && selectAllConfig.enable && /* @__PURE__ */ React.createElement(SelectAllOption, {
    ref: allOptionsRef,
    label: selectAllConfig.label,
    disabled
  }), React.Children.toArray(children).map((child, index) => {
    const _child = child;
    return cloneElement(_child, {
      ref(ref) {
        childrenRef.current[index] = ref;
      }
    });
  })));
};
export default forwardRef((props, ref) => {
  const { onChange, value = "", multiselect, ...restProps } = props;
  const { id } = props;
  const selectedValue = Array.isArray(value) ? value[value.length - 1] ?? "" : value;
  const selectedValues = (typeof value === "string" ? [selectedValue] : value).filter(Boolean);
  return /* @__PURE__ */ React.createElement(SelectProvider, {
    isMultiselect: !!multiselect,
    menuId: id,
    selectedValue,
    selectedValues,
    onChange
  }, /* @__PURE__ */ React.createElement(BaseSelect, {
    forwardedRef: ref,
    ...restProps
  }));
});
